import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MOOD_SCALE, TOPIC_TAGS } from '../../lib/constants';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { bandOf } from '../../ui/score';
import { TeacherFrame } from './TeacherFrame';
import { forecastPill, useTeacherHome } from './hooks';
import { errorText, rpc, type AppError, type ContextTag, type HistoryRow, type TeacherHomeData } from './api';
import { ReadinessBanner } from './ReadinessBanner';

// K5 (F2b): the school's extra student languages for this class.
type ClassLanguages = {
  enabled: boolean;
  school: { code: string; name_en: string; name_fi: string; name_sv: string; native_name: string; screen_status: 'reviewed' | 'draft' | 'none' }[];
  saved: string[] | null;
};

const LENGTHS = [10, 15, 20, 30, 45, 55, 75, 90, 120];

// I1 prepare (length, context tag, prediction, schedules) and the class's
// history of the teacher's own finished check-ins.
export function ClassPage() {
  const { t } = useI18n();
  const { classId = '' } = useParams<{ classId: string }>();
  const { data, error, reload } = useTeacherHome();
  const cls = data?.classes.find((c) => c.id === classId);
  return (
    <TeacherFrame title={cls ? t['ts.classTitle'](cls.label) : t['ts.classTitleBare']} active="classes">
      {() =>
        error ? (
          <p className="notice notice-block" role="alert">{errorText(t, error)}</p>
        ) : !data ? (
          <p role="status">{t.loading}</p>
        ) : !cls ? (
          <div className="panel p5">
            <p>{t['ts.classGone']}</p>
            <p><Link className="ts-link" to="/teacher">← {t['teacher.title']}</Link></p>
          </div>
        ) : (
          <ClassBody data={data} classId={classId} reload={reload} />
        )
      }
    </TeacherFrame>
  );
}

function ClassBody({ data, classId, reload }: { data: TeacherHomeData; classId: string; reload: () => void }) {
  const { t } = useI18n();
  const cls = data.classes.find((c) => c.id === classId)!;
  return (
    <>
      <p className="crumbs"><Link to="/teacher">← {t['teacher.title']}</Link></p>
      <ReadinessBanner state={data.school.state} />
      <div className="two-col">
        <PlanPanel data={data} classId={classId} />
        <div className="stack">
          <section className="panel p5" aria-labelledby="fc-title">
            <h2 className="panel-title" id="fc-title">{t['ts.fcTitle']}</h2>
            <p>
              <Pill kind={forecastPill(cls.forecast)}>{t[`ts.fc.${cls.forecast}` as 'ts.fc.likely']}</Pill>
            </p>
            <p className="form-hint">{t[`ts.fcHint.${cls.forecast}` as 'ts.fcHint.likely']}</p>
            <p className="form-hint">{cls.last_session ? t['ts.fcHow'] : t['r4.fcHowNoHistory']}</p>
          </section>
          {data.features.class_reflection_loop !== false && (
            <section className="panel p5" aria-labelledby="refl-title">
              <h2 className="panel-title" id="refl-title">{t['r6.tr.panelTitle']}</h2>
              <p className="form-hint">{t['r6.tr.panelHint']}</p>
              <p><Link className="btn btn-ghost" to={`/teacher/class/${classId}/reflection`}>{t['r6.tr.link']}</Link></p>
            </section>
          )}
          {data.features.session_schedules !== false && <SchedulePanel data={data} classId={classId} reload={reload} />}
        </div>
      </div>
      <HistoryPanel classId={classId} />
    </>
  );
}

function PlanPanel({ data, classId }: { data: TeacherHomeData; classId: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const announce = useAnnounce();
  const cls = data.classes.find((c) => c.id === classId)!;
  const max = data.school.session_max_minutes;
  const lengths = LENGTHS.filter((m) => m <= max);
  const [minutes, setMinutes] = useState(Math.min(55, max));
  const [tag, setTag] = useState<ContextTag | ''>('');
  const [mood, setMood] = useState<number | null>(null);
  const [topics, setTopics] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<AppError | null>(null);
  const [langs, setLangs] = useState<ClassLanguages | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const predictions = data.features.teacher_predictions !== false;
  const live = data.school.state === 'live';

  useEffect(() => {
    rpc<ClassLanguages>('teacher_class_languages', { p_class_id: classId }).then((r) => {
      if (r.error || !r.data.enabled) return setLangs(null);
      setLangs(r.data);
      setChosen(r.data.saved ?? r.data.school.map((l) => l.code));
    });
  }, [classId]);
  const setTopicsAllowed = TOPIC_TAGS.filter((tt) => data.question_set.topics.includes(tt.value));

  if (cls.open_session) {
    return (
      <section className="panel p5" aria-labelledby="plan-title">
        <h2 className="panel-title" id="plan-title">{t['ts.planTitle']}</h2>
        {cls.open_session.mine ? (
          <p>
            <Link className="btn btn-primary" to={`/teacher/session/${cls.open_session.id}`}>{t['ts.openProjector']}</Link>
          </p>
        ) : (
          <p className="notice notice-block">{t['ts.colleagueOpen']}</p>
        )}
      </section>
    );
  }

  async function start(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const r = await rpc<{ session_id: string; code: string }>('teacher_start_session', {
      p_class_id: classId,
      p_minutes: minutes,
      p_context_tag: tag || null,
      p_expected_mood: predictions && mood !== null ? mood : null,
      p_expected_topics: predictions && mood !== null ? topics : null,
      p_languages: langs && langs.school.length > 0 ? chosen : null,
    });
    setBusy(false);
    if (r.error) {
      setErr(r.error);
      return;
    }
    announce(t['session.opened'](cls.label, r.data.code.split('').join(' ')));
    navigate(`/teacher/session/${r.data.session_id}`);
  }

  return (
    <section className="panel p5" aria-labelledby="plan-title">
      <h2 className="panel-title" id="plan-title">{t['ts.planTitle']}</h2>
      <form className="form" onSubmit={start} noValidate>
        <div className="form-row">
          <div className="field-group">
            <label className="field-label" htmlFor="plan-length">{t['ts.length']}</label>
            <div className="select-wrap">
              <select id="plan-length" className="select" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
                {lengths.map((m) => (
                  <option key={m} value={m}>{t['ts.minutes'](m)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="field-group">
            <label className="field-label" htmlFor="plan-tag">{t['ts.tag']}</label>
            <div className="select-wrap">
              <select id="plan-tag" className="select" value={tag} onChange={(e) => setTag(e.target.value as ContextTag | '')} aria-describedby="plan-tag-hint">
                <option value="">{t['ts.tagNone']}</option>
                {data.context_tags.map((c) => (
                  <option key={c} value={c}>{t[`ts.tag.${c}` as 'ts.tag.normal']}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <p className="form-hint" id="plan-tag-hint">{t['ts.tagHint']}</p>

        {predictions && (
          <fieldset className="fieldset">
            <legend className="field-label">{t['ts.predictTitle']}</legend>
            <p className="form-hint">{t['ts.predictHint']}</p>
            <div className="chip-row">
              {MOOD_SCALE.map((m) => (
                <label key={m.value} className={`chip-check${mood === m.value ? ' on' : ''}`}>
                  <input type="radio" name="predict-mood" value={m.value} checked={mood === m.value} onChange={() => setMood(m.value)} />
                  <span aria-hidden="true">{m.emoji}</span> {t[`mood.${m.value}` as 'mood.1']}
                </label>
              ))}
            </div>
            {mood !== null && (
              <>
                <p className="field-label" id="predict-topics">{t['ts.predictTopics']}</p>
                <div className="chip-row" role="group" aria-labelledby="predict-topics">
                  {setTopicsAllowed.map((tt) => {
                    const on = topics.includes(tt.value);
                    return (
                      <label key={tt.value} className={`chip-check${on ? ' on' : ''}`}>
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => setTopics((p) => (on ? p.filter((x) => x !== tt.value) : [...p, tt.value]))}
                        />
                        <span aria-hidden="true">{tt.emoji}</span> {t[`topic.${tt.value}` as 'topic.exams']}
                      </label>
                    );
                  })}
                </div>
                <button type="button" className="link-btn" onClick={() => { setMood(null); setTopics([]); }}>
                  {t['ts.predictClear']}
                </button>
              </>
            )}
          </fieldset>
        )}

        {langs && langs.school.length > 0 && <LanguageChoice langs={langs} chosen={chosen} setChosen={setChosen} />}

        {data.question_set.is_draft && <p className="notice notice-block"><Pill kind="warn">{t['ts.draft']}</Pill> {t['ts.draftSet'](data.question_set.label)}</p>}
        {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
        <div className="form-actions">
          <Link className="btn btn-ghost" to="/teacher">{t['form.cancel']}</Link>
          <button type="submit" className="btn btn-primary" disabled={busy || !live} aria-describedby={live ? undefined : 'ready-title'}>
            {busy ? t['pa.working'] : t['btn.startCheckin']}
          </button>
        </div>
      </form>
    </section>
  );
}

// Finnish, Swedish and English are always on; the teacher switches off the
// extra languages this class doesn't need. Remembered per class on start.
function LanguageChoice({ langs, chosen, setChosen }: { langs: ClassLanguages; chosen: string[]; setChosen: (f: (p: string[]) => string[]) => void }) {
  const { t, lang } = useI18n();
  const name = (l: ClassLanguages['school'][number]) => (lang === 'fi' ? l.name_fi : lang === 'sv' ? l.name_sv : l.name_en);
  return (
    <fieldset className="fieldset">
      <legend className="field-label">{t['r4.tl.title']}</legend>
      <p className="form-hint">{t['r4.tl.hint']}</p>
      <div className="chip-row">
        {(['fi', 'sv', 'en'] as const).map((c) => (
          <span key={c} className="chip-check on chip-fixed">
            {t[`r4.lang.${c}`]} <span className="text-muted">· {t['r4.tl.always']}</span>
          </span>
        ))}
        {langs.school.map((l) => {
          const on = chosen.includes(l.code);
          return (
            <label key={l.code} className={`chip-check${on ? ' on' : ''}`}>
              <input type="checkbox" checked={on} onChange={() => setChosen((p) => (on ? p.filter((x) => x !== l.code) : [...p, l.code]))} />
              <span lang={l.code}>{l.native_name}</span> <span className="text-muted">({name(l)}{l.screen_status === 'draft' ? `, ${t['r4.tl.draft']}` : l.screen_status === 'none' ? `, ${t['r4.tl.english']}` : ''})</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function SchedulePanel({ data, classId, reload }: { data: TeacherHomeData; classId: string; reload: () => void }) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const mine = data.schedules.filter((s) => s.class_id === classId);
  const [day, setDay] = useState(1);
  const [time, setTime] = useState('08:15');
  const [minutes, setMinutes] = useState(15);
  const [err, setErr] = useState<AppError | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const r = await rpc('teacher_save_schedule', { p_class_id: classId, p_weekday: day, p_start: time, p_minutes: minutes });
    setBusy(false);
    if (r.error) {
      setErr(r.error);
      return;
    }
    announce(t['ts.schedSaved']);
    reload();
  }

  async function end(id: string) {
    const r = await rpc('teacher_end_schedule', { p_schedule_id: id });
    if (r.error) setErr(r.error);
    else {
      announce(t['ts.schedEnded']);
      reload();
    }
  }

  return (
    <section className="panel p5" aria-labelledby="sched-title">
      <h2 className="panel-title" id="sched-title">{t['ts.schedTitle']}</h2>
      <p className="form-hint">{t['ts.schedHint']}</p>
      {mine.length > 0 && (
        <ul className="sched-list">
          {mine.map((s) => (
            <li key={s.id}>
              <span>{t[`ts.day${s.weekday}` as 'ts.day1']} {s.start_time} · {t['ts.minutes'](s.minutes)}</span>
              <button type="button" className="link-btn" onClick={() => end(s.id)}>
                {t['ts.schedEnd']}
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="form" onSubmit={save} noValidate>
        <div className="form-row">
          <div className="field-group">
            <label className="field-label" htmlFor="sched-day">{t['ts.schedDay']}</label>
            <div className="select-wrap">
              <select id="sched-day" className="select" value={day} onChange={(e) => setDay(Number(e.target.value))}>
                {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                  <option key={d} value={d}>{t[`ts.day${d}` as 'ts.day1']}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="field-group">
            <label className="field-label" htmlFor="sched-time">{t['ts.schedTime']}</label>
            <input id="sched-time" className="field" type="time" value={time} onChange={(e) => setTime(e.target.value)} required />
          </div>
          <div className="field-group">
            <label className="field-label" htmlFor="sched-len">{t['ts.length']}</label>
            <div className="select-wrap">
              <select id="sched-len" className="select" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
                {LENGTHS.filter((m) => m <= data.school.session_max_minutes).map((m) => (
                  <option key={m} value={m}>{t['ts.minutes'](m)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
        <div className="form-actions">
          <button type="submit" className="btn btn-ghost" disabled={busy}>{t['ts.schedSave']}</button>
        </div>
      </form>
    </section>
  );
}

function HistoryPanel({ classId }: { classId: string }) {
  const { t, locale, num } = useI18n();
  const [rows, setRows] = useState<HistoryRow[] | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const load = useCallback(async () => {
    const r = await rpc<HistoryRow[]>('teacher_class_history', { p_class_id: classId });
    if (r.error) setErr(r.error);
    else setRows(r.data);
  }, [classId]);
  useEffect(() => {
    load();
  }, [load]);
  const dateFmt = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <section className="panel p5" aria-labelledby="hist-title">
      <h2 className="panel-title" id="hist-title">{t['ts.histTitle']}</h2>
      <p className="form-hint">{t['ts.histHint']}</p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      {rows && rows.length === 0 && <p className="text-muted">{t['ts.histNone']}</p>}
      {rows && rows.length > 0 && (
        <ul className="hist-list">
          {rows.map((r) => (
            <li key={r.session_id} className="hist-row">
              <span className="hist-date">{dateFmt.format(new Date(r.started_at))}</span>
              {r.context_tag && <Pill kind="neutral">{t[`ts.tag.${r.context_tag}` as 'ts.tag.normal']}</Pill>}
              <span className="hist-status">
                {r.status === 'ok' && r.mean !== null ? (
                  <>
                    <strong>{num(r.mean)}/5</strong> <Pill kind={bandOf(r.mean).key}>{t[`band.${bandOf(r.mean).key}`]}</Pill>
                  </>
                ) : (
                  <span className="text-muted">{t[`ts.st.${r.status}` as 'ts.st.insufficient']}</span>
                )}
              </span>
              <span className="text-muted">{t['results.responses'](r.total)}</span>
              {r.expected_mood !== null && <span className="text-muted">{t['ts.youExpected'](r.expected_mood)}</span>}
              {r.actions && r.actions.length > 0 && (
                <span className="text-muted">{t['act.histDid'](r.actions.map((a) => t[`act.${a}` as 'act.other']).join(', '))}</span>
              )}
              <Link to={`/teacher/session/${r.session_id}`}>
                {t['ts.view']}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
